import { useMemo } from 'react';
import type { QueryMessage } from '../../core/types/workspaceTypes';
import { buildOutputLines } from '../utils/outputPanelUtils';

interface OutputPanelProps {
  messages?: QueryMessage[];
  resultSetCount: number;
  className?: string;
  onSelectResultSet?: (index: number) => void;
  testId?: string;
}

function messageClass(type: QueryMessage['type']): string {
  switch (type) {
    case 'error':
      return 'text-destructive';
    case 'warning':
      return 'text-warning';
    case 'success':
      return 'text-foreground';
    case 'info':
    default:
      return 'text-foreground';
  }
}

export function OutputPanel({
  messages = [],
  resultSetCount,
  className = '',
  onSelectResultSet,
  testId = 'output-result-pane',
}: OutputPanelProps) {
  const lines = useMemo(
    () => buildOutputLines(messages, resultSetCount),
    [messages, resultSetCount],
  );

  if (lines.length === 0) {
    return (
      <div
        className={`w-full h-full flex items-center justify-center ${className}`}
        data-testid={testId}
      >
        <p className="text-muted-foreground text-sm">No output to display.</p>
      </div>
    );
  }

  return (
    <div className={`w-full h-full overflow-auto p-3 ${className}`} data-testid={testId}>
      <div className="font-mono text-xs leading-relaxed flex flex-col gap-1">
        {lines.map((line, i) => {
          if (line.kind === 'placeholder') {
            const oneBased = line.index + 1;
            const label = `[Result ${oneBased}]`;
            return (
              <button
                key={`placeholder-${line.index}-${i}`}
                type="button"
                onClick={() => onSelectResultSet?.(line.index)}
                disabled={!onSelectResultSet}
                className="self-start px-2 py-0.5 rounded border border-border bg-muted/40 hover:bg-muted/70 text-foreground transition-colors disabled:cursor-default disabled:hover:bg-muted/40"
                data-testid={`output-results-reference-${oneBased}`}
              >
                {label}
              </button>
            );
          }
          return (
            <div
              key={`msg-${line.message.id}-${i}`}
              className={`whitespace-pre-wrap ${messageClass(line.message.type)}`}
            >
              {line.message.content}
            </div>
          );
        })}
      </div>
    </div>
  );
}
